import {
  useCallback,
  useEffect,
  useState,
  FC,
  ChangeEventHandler,
} from 'react';
import { useParams } from 'react-router-dom';
import { Flex, Box } from '@rebass/emotion';

import Card from '@app/components/Card';
import useLocalizationCountry from '@app/hooks/useLocalizationCountry';
import { Button } from '@app/shop/components/Button';
import { H3 } from '@app/shop/components/typography';
import { Magento2_CountryCodeEnum } from '@__generated__/globalTypes';

type SelectHandler = ChangeEventHandler<HTMLSelectElement>;
type StoreParams = { country: string; language: string };

const supportedCountries: Magento2_CountryCodeEnum[] = [
  Magento2_CountryCodeEnum.AU,
  Magento2_CountryCodeEnum.CA,
  Magento2_CountryCodeEnum.CH,
  Magento2_CountryCodeEnum.CN,
  Magento2_CountryCodeEnum.DE,
  Magento2_CountryCodeEnum.ES,
  Magento2_CountryCodeEnum.FR,
  Magento2_CountryCodeEnum.GB,
  Magento2_CountryCodeEnum.JP,
  Magento2_CountryCodeEnum.NO,
  Magento2_CountryCodeEnum.RU,
  Magento2_CountryCodeEnum.US,
];
const supportedLangs = ['en', 'de', 'es', 'fr', 'jp', 'zh'];

const StoreSelection: FC = () => {
  const { language: paramLanguage } = useParams<{ language: string }>();
  const {
    country: localizationCountry,
    setLocalizationCountry,
  } = useLocalizationCountry();
  const [storeParams, setStoreParams] = useState<StoreParams>({
    language: paramLanguage || 'en',
    country: localizationCountry || Magento2_CountryCodeEnum.US,
  });
  const { country, language: storeLanguage } = storeParams;

  const assignStoreCodeParams = useCallback(() => {
    setLocalizationCountry(country);
    window.location.replace(`/${storeLanguage}/shop`);
  }, [country, storeLanguage, setLocalizationCountry]);

  const handleCountryCodeSelectChange = useCallback<SelectHandler>((event) => {
    const { value } = event.currentTarget;

    setStoreParams((current) => ({ ...current, country: value }));
  }, []);

  const handleLanguageSelectChange = useCallback<SelectHandler>((event) => {
    const { value } = event.currentTarget;

    setStoreParams((current) => ({ ...current, language: value }));
  }, []);

  useEffect(() => {
    if (!localizationCountry) return;

    setStoreParams((current) =>
      current.country === localizationCountry
        ? current
        : { ...current, country: localizationCountry }
    );
  }, [localizationCountry]);

  return (
    <Box p={2}>
      <Card>
        <Flex alignItems="center">
          <H3>Change store</H3>
          <Box mr={2} ml={4}>
            <label htmlFor="country-code-select">Country: </label>
            <select
              name="country-code-select"
              value={country}
              onChange={handleCountryCodeSelectChange}
            >
              {supportedCountries.map((countryCode) => (
                <option key={countryCode} value={countryCode}>
                  {countryCode}
                </option>
              ))}
            </select>
          </Box>
          <Box mr={4}>
            <label htmlFor="lang-select">Language: </label>
            <select
              name="lang-select"
              value={storeLanguage}
              onChange={handleLanguageSelectChange}
            >
              {supportedLangs.map((supportedLang) => (
                <option key={supportedLang} value={supportedLang}>
                  {supportedLang}
                </option>
              ))}
            </select>
          </Box>
          <Button
            type="primary"
            onClick={assignStoreCodeParams}
            data-testid="apply-store-code-button"
          >
            Apply
          </Button>
        </Flex>
      </Card>
    </Box>
  );
};

export default StoreSelection;
